import { withZod } from '@remix-validated-form/with-zod'
import { useState } from 'react'
import { z } from 'zod'
import { zfd } from 'zod-form-data'
import Button from '../Button'
import FormCheckBox from '../form/FormCheckBox'
import FormCombo from '../form/FormCombo'
import FormHiddenInput from '../form/FormHiddenInput'
import FormInput from '../form/FormInput'
import FormTextArea from '../form/FormTextArea'
import { SmartForm } from '../form/SmartForm'
import { Toggle } from '../Toggle'
import { getFieldTypeDisplayName } from './CustomQuestion'
import { CustomQuestionType } from 'app/types/entities'
import { ObjectId } from 'lib/ObjectId'
import { OptionType } from 'types/OptionType'

const revalidateFieldName = <T extends z.ZodType<{ optIn?: boolean; fieldName?: string }>>(schema: T) => {
  return schema.refine(
    (data) => {
      // Field name is optional for opt-in fields
      if (data.optIn) return true
      // Otherwise, field name is required
      return data.fieldName && data.fieldName.trim() !== ``
    },
    {
      message: `Field name is required`,
      path: [`fieldName`],
    },
  )
}

const baseSchema = z.object({
  fieldName: z.string().optional(),
  required: zfd.checkbox(),
  optIn: z
    .string()
    .default(`false`)
    .transform((val) => val === `true`)
    .pipe(z.boolean()),
  fieldType: z.enum([`one-line`, `multiple-lines`, `checkbox`, `opt-in`]),
  description: z.string().max(500, { message: `Description must be 500 characters or less` }).optional(),
  enabled: z
    .string()
    .default(`true`)
    .transform((val) => val === `true`)
    .pipe(z.boolean()),
})

const createSchema = revalidateFieldName(baseSchema)

const createValidator = withZod(createSchema)

const editSchema = revalidateFieldName(baseSchema.extend({ id: z.string().min(1, { message: `An id is required` }) }))

const editValidator = withZod(editSchema)

export default function QuestionPanel({
  locationId,
  question,
  onSaved,
  onQuestionDelete,
  onCancel,
}: {
  locationId: string
  question?: CustomQuestionType
  onSaved: (data: CustomQuestionType) => Promise<void>
  onQuestionDelete?: (id: string) => Promise<void>
  onCancel: () => void
}) {
  const isSaving = false
  const [selectedFieldType, setSelectedFieldType] = useState<OptionType | undefined>(
    question
      ? {
          id: question.optIn ? `opt-in` : question.fieldType,
          name: question.optIn ? `Opt-In` : getFieldTypeDisplayName(question.fieldType),
          value: question.optIn ? `opt-in` : question.fieldType,
        }
      : undefined,
  )
  const [active, setActive] = useState(question ? question.enabled : true)
  const [isOptIn, setIsOptIn] = useState(question?.optIn ?? false)

  return (
    <SmartForm
      className="!mt-0 !max-w-none !gap-y-0"
      validator={question ? editValidator : createValidator}
      hiddenInputs={
        question
          ? [
              { name: `id`, value: question.id },
              { name: `locationId`, value: locationId },
            ]
          : [{ name: `locationId`, value: locationId }]
      }
      preventSubmit
      onSubmit={async (data) => {
        let questionData = question ? data : { ...data, id: ObjectId() }

        // Convert "opt-in" field type to checkbox + optIn flag
        if (data.fieldType === `opt-in` || selectedFieldType?.value === `opt-in`) {
          questionData = {
            ...questionData,
            fieldType: `checkbox`,
            optIn: true,
            required: false, // Opt-in fields are never required
          }
        } else {
          // Ensure optIn is false for non-opt-in fields
          questionData = {
            ...questionData,
            optIn: false,
          }
        }

        await onSaved(questionData)
      }}
      hideButtons
    >
      <div className="col-span-6 flex flex-col gap-y-6 p-4">
        <FormHiddenInput name="enabled" value={`${active}`} />
        <FormHiddenInput name="optIn" value={`${isOptIn}`} />
        <FormInput
          name="fieldName"
          label="Field name"
          required={!isOptIn}
          placeholder={isOptIn ? `Optional for Opt-In` : `Required: please add a field name`}
          labelClassName="text-label-sm text-strong"
          defaultValue={question?.fieldName}
          cols={6}
        />
        {!isOptIn && (
          <FormCheckBox name="required" label="Required" defaultChecked={question?.required ?? false} cols={6} />
        )}
        <FormCombo
          cols={6}
          name="fieldType"
          label="Field type"
          placeholder="Select a field type"
          options={[
            { id: `one-line`, name: `One line`, value: `one-line` },
            { id: `multiple-lines`, name: `Multiple lines`, value: `multiple-lines` },
            { id: `checkbox`, name: `Checkbox`, value: `checkbox` },
            { id: `opt-in`, name: `Opt-In`, value: `opt-in` },
          ]}
          value={selectedFieldType}
          onChange={(option) => {
            setSelectedFieldType(option)

            // When "Opt-In" is selected, set isOptIn to true
            if (option.value === `opt-in`) {
              setIsOptIn(true)
            } else {
              // When switching away from opt-in, unset it
              setIsOptIn(false)
            }
          }}
          labelClassName="text-label-sm text-strong"
        />
        {isOptIn && (
          <FormTextArea
            name="description"
            label="Description"
            labelClassName="text-label-sm text-strong"
            placeholder="Terms, conditions, opt-in details etc."
            defaultValue={question?.description}
            cols={6}
            maxLength={500}
            className="h-40 resize-none"
          />
        )}
        <div className="flex items-center justify-between text-label-sm text-strong">
          <p>Status</p>
          <Toggle label="On" defaultValue={question?.enabled ?? true} handleToggle={(val) => setActive(val)} />
        </div>
      </div>
      <div
        className={`relative bottom-0 z-10 col-span-6 flex rounded-b-lg border-b border-t border-default px-4 py-3 ${
          question ? `justify-between` : `justify-end`
        } bg-surface`}
      >
        {!!question && (
          <Button
            label="Delete"
            size="md"
            mode="danger"
            onClick={async () => {
              if (onQuestionDelete) {
                await onQuestionDelete(question.id!)
              }
            }}
          />
        )}
        <div className="flex justify-end gap-4">
          <Button label="Cancel" mode="white" size="md" onClick={() => onCancel()} />
          <Button type="submit" label={question ? `Confirm` : `Add`} mode="primary" loading={isSaving} size="md" />
        </div>
      </div>
    </SmartForm>
  )
}
